import { useEffect, useMemo, useRef, useState } from 'react';
import type { FixPuzzle } from '@continuum/show-core';
import { Hero, mono, sans } from '../ui/atoms.js';
import { FIX_COPY } from '../ui/fixText.js';
import type { Lang } from '../ui/text.js';

/**
 * The FIX puzzles (Marek, 2026-09-29): what a guest handed FIX does to fix an alarm, in place of
 * holding a button together. Three kinds, a different one per alarm and guest (show-core
 * `fixPuzzleFor`), each short and of medium difficulty, each fitting one phone screen:
 *
 *   circuit — RECONNECT THE CIRCUIT: a 3 × 4 grid of path tiles, tap to turn them until a line runs
 *             from START to END;
 *   code    — REMEMBER THE CODE: four digits for five seconds, then typed on a keypad;
 *   order   — PUT THEM BACK IN ORDER: four symbols in order for five seconds, then shuffled, tapped
 *             back in the order they came.
 *
 * Easier since 2026-10-06 (Marek: "memorize numbers is hard"): four, not five, shown five seconds, not
 * three; and a wrong try shows the same code or the same symbols again rather than a new set. Everything
 * is made from the seed, so a phone that reloads gets the same puzzle. There is no lockout, only the
 * alarm's clock.
 */

// ------------------------------------------------------------------ made from the seed

/** A small, even random stream from a number, the same on every phone. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0 || 1;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A round's own seed: the puzzle's, moved on for every try. */
export function roundSeed(seed: number, round: number): number {
  return (seed ^ Math.imul(round + 1, 0x9e3779b1)) >>> 0;
}

/** Directions round a tile: north, east, south, west. */
type Dir = 0 | 1 | 2 | 3;
const STEP: Record<Dir, [number, number]> = { 0: [-1, 0], 1: [0, 1], 2: [1, 0], 3: [0, -1] };

/** A path tile: a straight (I) or a corner (L), turned `rot` quarter turns clockwise. */
export interface CircuitTile {
  kind: 'I' | 'L';
  rot: number;
}
export interface CircuitBoard {
  rows: number;
  cols: number;
  tiles: CircuitTile[];
}

export const CIRCUIT = { rows: 3, cols: 4 } as const;

/** Which sides a tile opens to, as it is turned now. */
export function openings(tile: CircuitTile): Dir[] {
  const base: Dir[] = tile.kind === 'I' ? [0, 2] : [0, 1];
  return base.map((d) => ((d + tile.rot) % 4) as Dir);
}

/**
 * Whether a line runs from START (the left side of the top-left tile) to END (the right side of the
 * bottom-right one), through tiles that open to each other. Any such line counts, not only the one
 * the puzzle was made from.
 */
export function circuitJoined(board: CircuitBoard): boolean {
  const at = (r: number, c: number) => board.tiles[r * board.cols + c]!;
  if (!openings(at(0, 0)).includes(3)) return false;
  const seen = new Set<number>([0]);
  const queue: [number, number][] = [[0, 0]];
  while (queue.length > 0) {
    const [r, c] = queue.shift()!;
    if (r === board.rows - 1 && c === board.cols - 1 && openings(at(r, c)).includes(1)) return true;
    for (const d of openings(at(r, c))) {
      const [dr, dc] = STEP[d];
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nc < 0 || nr >= board.rows || nc >= board.cols) continue;
      if (!openings(at(nr, nc)).includes(((d + 2) % 4) as Dir)) continue;
      const key = nr * board.cols + nc;
      if (seen.has(key)) continue;
      seen.add(key);
      queue.push([nr, nc]);
    }
  }
  return false;
}

/** A circuit to reconnect: a winding line from START to END laid in tiles, then every tile turned at random. */
export function circuitBoard(seed: number): CircuitBoard {
  const { rows, cols } = CIRCUIT;
  const rand = seeded(seed);
  // A winding line from the top-left to the bottom-right, found by a random walk that backs up.
  let path: [number, number][] = [];
  for (let attempt = 0; attempt < 40; attempt++) {
    const found = walk(rows, cols, rand);
    if (found && (found.length >= rows + cols || attempt === 39)) {
      path = found;
      break;
    }
  }
  const tiles: CircuitTile[] = Array.from({ length: rows * cols }, () => ({
    kind: rand() < 0.5 ? 'I' : 'L',
    rot: Math.floor(rand() * 4),
  }));
  path.forEach(([r, c], i) => {
    const from: Dir = i === 0 ? 3 : dirTo(path[i]!, path[i - 1]!);
    const to: Dir = i === path.length - 1 ? 1 : dirTo(path[i]!, path[i + 1]!);
    tiles[r * cols + c] = (from + 2) % 4 === to ? { kind: 'I', rot: from % 2 === 0 ? 0 : 1 } : { kind: 'L', rot: (from + 1) % 4 === to ? from : to };
  });
  const board: CircuitBoard = { rows, cols, tiles: tiles.map((t) => ({ ...t, rot: Math.floor(rand() * 4) })) };
  // Never handed over already joined.
  for (let i = 0; circuitJoined(board) && i < 12; i++) {
    const [r, c] = path[i % path.length]!;
    const t = board.tiles[r * cols + c]!;
    t.rot = (t.rot + 1) % 4;
  }
  return board;
}

function dirTo(from: [number, number], to: [number, number]): Dir {
  if (to[0] < from[0]) return 0;
  if (to[1] > from[1]) return 1;
  if (to[0] > from[0]) return 2;
  return 3;
}

function walk(rows: number, cols: number, rand: () => number): [number, number][] | null {
  const path: [number, number][] = [[0, 0]];
  const seen = new Set<string>(['0,0']);
  const tried = new Map<string, Dir[]>();
  for (let steps = 0; steps < 400 && path.length > 0; steps++) {
    const [r, c] = path[path.length - 1]!;
    if (r === rows - 1 && c === cols - 1) return path;
    const key = `${r},${c}`;
    if (!tried.has(key)) {
      const dirs: Dir[] = [0, 1, 2, 3];
      for (let i = dirs.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [dirs[i], dirs[j]] = [dirs[j]!, dirs[i]!];
      }
      tried.set(key, dirs);
    }
    const options = tried.get(key)!;
    const d = options.shift();
    if (d === undefined) {
      path.pop();
      seen.delete(key);
      continue;
    }
    const [dr, dc] = STEP[d];
    const next: [number, number] = [r + dr, c + dc];
    if (next[0] < 0 || next[1] < 0 || next[0] >= rows || next[1] >= cols || seen.has(`${next[0]},${next[1]}`)) continue;
    path.push(next);
    seen.add(`${next[0]},${next[1]}`);
  }
  return null;
}

/** How many digits, and how many symbols, there are to remember. */
export const MEMORY_LENGTH = 4;

/** REMEMBER THE CODE's digits. */
export function codeFor(seed: number): string {
  const rand = seeded(seed);
  return Array.from({ length: MEMORY_LENGTH }, () => String(Math.floor(rand() * 10))).join('');
}

const SYMBOLS = ['●', '▲', '■', '◆', '★', '✚', '♥', '☾'] as const;
const SYMBOL_COLOURS: Record<string, string> = {
  '●': '#ff6b6b',
  '▲': '#ffd400',
  '■': '#3fa9ff',
  '◆': '#4fe07a',
  '★': '#ffb547',
  '✚': '#ff8fd8',
  '♥': '#ff4d7a',
  '☾': '#c8bcff',
};

/** PUT THEM BACK IN ORDER: different symbols, in the order to remember, and the order they are shown in after. */
export function orderFor(seed: number): { order: string[]; shuffled: string[] } {
  const rand = seeded(seed);
  const pool = [...SYMBOLS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  const order = pool.slice(0, MEMORY_LENGTH);
  const shuffled = [...order];
  do {
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
    }
  } while (shuffled.every((x, i) => x === order[i]));
  return { order, shuffled };
}

// ------------------------------------------------------------------ on the phone

/** How long the code and the symbols show before they go. */
export const SHOW_MS = 5_000;

/**
 * One FIX puzzle, played until it is solved: a wrong try shows the same code or symbols again (the
 * circuit has no wrong try). Calls `onSolved` once.
 */
export function FixPuzzleBoard({
  kind,
  seed,
  lang,
  onSolved,
}: {
  kind: FixPuzzle;
  seed: number;
  lang: Lang;
  onSolved: () => void;
}) {
  const f = FIX_COPY[lang];
  const [round, setRound] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const done = useRef(false);
  const solved = () => {
    if (done.current) return;
    done.current = true;
    onSolved();
  };
  const wrong = () => {
    setNote(f.wrong);
    setRound((n) => n + 1);
  };
  // The same puzzle again after a wrong try: only the remount (`key`) starts it over.
  const s = roundSeed(seed, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Hero style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.05 }}>{f.puzzleTitle[kind]}</Hero>
      <div style={{ fontFamily: sans, fontSize: 16, fontWeight: 600, lineHeight: 1.3, color: '#ffd1da' }}>{f.puzzleHow[kind]}</div>
      {note ? <div style={{ fontFamily: sans, fontSize: 17, fontWeight: 700, color: '#ffb547' }}>{note}</div> : null}
      {kind === 'circuit' ? (
        <Circuit key={round} seed={s} lang={lang} onDone={solved} />
      ) : kind === 'code' ? (
        <Code key={round} seed={s} lang={lang} onDone={solved} onWrong={wrong} />
      ) : (
        <Order key={round} seed={s} lang={lang} onDone={solved} onWrong={wrong} />
      )}
    </div>
  );
}

function Circuit({ seed, lang, onDone }: { seed: number; lang: Lang; onDone: () => void }) {
  const f = FIX_COPY[lang];
  const [board, setBoard] = useState(() => circuitBoard(seed));
  const joined = circuitJoined(board);
  // Joined: a moment to see the line light up, then done. The screen around it redraws every half
  // second, so the timer keeps to the joining, not to the latest `onDone`.
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (!joined) return;
    const id = setTimeout(() => done.current(), 500);
    return () => clearTimeout(id);
  }, [joined]);
  const turn = (i: number) => {
    if (joined) return;
    setBoard((b) => ({ ...b, tiles: b.tiles.map((t, k) => (k === i ? { ...t, rot: (t.rot + 1) % 4 } : t)) }));
  };
  const line = joined ? '#4fd8c2' : '#ffffff';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 2, color: '#ffd1da' }}>▶ {f.start}</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${board.cols}, minmax(0, 1fr))`, gap: 6, width: '100%', maxWidth: 300, alignSelf: 'center' }}>
        {board.tiles.map((tile, i) => (
          <button
            key={i}
            onClick={() => turn(i)}
            aria-label={`tile ${i + 1}`}
            style={{
              aspectRatio: '1',
              minWidth: 0,
              padding: 0,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,.25)',
              background: 'rgba(255,255,255,.05)',
            }}
          >
            <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
              {openings(tile).map((d) => {
                const [x, y] = d === 0 ? [50, 0] : d === 1 ? [100, 50] : d === 2 ? [50, 100] : [0, 50];
                return <line key={d} x1={50} y1={50} x2={x} y2={y} stroke={line} strokeWidth={16} strokeLinecap="round" />;
              })}
              <circle cx={50} cy={50} r={9} fill={line} />
            </svg>
          </button>
        ))}
      </div>
      <div style={{ alignSelf: 'flex-end', fontFamily: mono, fontSize: 12, letterSpacing: 2, color: '#ffd1da' }}>{f.finish} ▶</div>
    </div>
  );
}

function Code({ seed, lang, onDone, onWrong }: { seed: number; lang: Lang; onDone: () => void; onWrong: () => void }) {
  const f = FIX_COPY[lang];
  const code = useMemo(() => codeFor(seed), [seed]);
  const [showing, setShowing] = useState(true);
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setShowing(false), SHOW_MS);
    return () => clearTimeout(id);
  }, []);
  const key = (digit: string) => {
    const next = `${typed}${digit}`.slice(0, MEMORY_LENGTH);
    setTyped(next);
    if (next.length < MEMORY_LENGTH) return;
    if (next === code) onDone();
    else onWrong();
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontFamily: sans, fontSize: 18, fontWeight: 700 }}>{showing ? f.remember : f.typeIt}</div>
      <div style={{ fontFamily: mono, fontSize: 40, fontWeight: 700, letterSpacing: 8, color: '#ffffff', minHeight: 52 }}>
        {showing ? code.split('').join(' ') : typed.padEnd(MEMORY_LENGTH, '·').split('').join(' ')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, opacity: showing ? 0.35 : 1 }}>
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((k) =>
          k === '' ? (
            <span key="gap" />
          ) : (
            <button
              key={k}
              disabled={showing}
              onClick={() => (k === '⌫' ? setTyped((t) => t.slice(0, -1)) : key(k))}
              style={{
                minHeight: 58,
                borderRadius: 14,
                border: '1px solid rgba(255,255,255,.2)',
                background: 'rgba(255,255,255,.06)',
                color: '#ffffff',
                fontFamily: mono,
                fontSize: 26,
                fontWeight: 700,
              }}
            >
              {k}
            </button>
          ),
        )}
      </div>
    </div>
  );
}

function Order({ seed, lang, onDone, onWrong }: { seed: number; lang: Lang; onDone: () => void; onWrong: () => void }) {
  const f = FIX_COPY[lang];
  const { order, shuffled } = useMemo(() => orderFor(seed), [seed]);
  const [showing, setShowing] = useState(true);
  const [picked, setPicked] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setShowing(false), SHOW_MS);
    return () => clearTimeout(id);
  }, []);
  const tap = (symbol: string) => {
    if (showing) return;
    if (symbol !== order[picked]) {
      onWrong();
      return;
    }
    if (picked + 1 === order.length) onDone();
    setPicked(picked + 1);
  };
  const shown = showing ? order : shuffled;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontFamily: sans, fontSize: 18, fontWeight: 700 }}>{showing ? f.remember : f.tapInOrder}</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${MEMORY_LENGTH}, minmax(0, 1fr))`, gap: 8 }}>
        {shown.map((symbol, i) => {
          const used = !showing && order.indexOf(symbol) < picked;
          return (
            <button
              key={`${symbol}-${i}`}
              disabled={showing || used}
              onClick={() => tap(symbol)}
              style={{
                aspectRatio: '1',
                minWidth: 0,
                padding: 0,
                borderRadius: 12,
                border: `2px solid ${SYMBOL_COLOURS[symbol] ?? '#ffffff'}`,
                background: used ? `${SYMBOL_COLOURS[symbol] ?? '#ffffff'}55` : 'rgba(255,255,255,.05)',
                color: SYMBOL_COLOURS[symbol] ?? '#ffffff',
                fontSize: 30,
                fontWeight: 700,
                opacity: used ? 0.5 : 1,
              }}
            >
              {symbol}
            </button>
          );
        })}
      </div>
      {!showing && (
        <div style={{ fontFamily: mono, fontSize: 13, color: '#9db3c2' }}>
          {picked} / {order.length}
        </div>
      )}
    </div>
  );
}
